const POSITIONING_BY_CODE = {
  // Current catalog codes
  free: 'Try it out',
  starter_monthly: 'Start your preparation',
  pro_monthly: 'Active job search',
  bars_premium_monthly: 'Advanced BARS analytics',
  tester_full_eur_monthly: 'Full access for testers',
  // Deprecated codes (backward compatibility)
  starter_free: 'Try it out',
  sprint_weekly: 'Urgent preparation',
  executive_quarterly: 'Leadership track',
};

function capitalize(value) {
  if (!value) return '';
  return value.charAt(0).toUpperCase() + value.slice(1);
}

export function extractPlanCodeFromProductName(productName) {
  if (!productName) return null;
  const match = String(productName).trim().toLowerCase().match(/\[([a-z0-9_]+)\]\s*$/);
  return match?.[1] || null;
}

export function getCadenceFromPlanCode(planCode, priceUsd = 0) {
  const normalized = String(planCode || '').toLowerCase();
  if (Number(priceUsd) <= 0) {
    return 'forever';
  }
  if (normalized.endsWith('_weekly')) return 'per week';
  if (normalized.endsWith('_monthly')) return 'per month';
  if (normalized.endsWith('_quarterly')) return 'per quarter';
  if (normalized.endsWith('_yearly')) return 'per year';
  if (normalized.endsWith('_lifetime')) return 'lifetime';
  return 'per period';
}

export function getDurationDaysFromPlanCode(planCode) {
  const normalized = String(planCode || '').toLowerCase();
  if (normalized.endsWith('_weekly')) return 7;
  if (normalized.endsWith('_monthly')) return 30;
  if (normalized.endsWith('_quarterly')) return 90;
  if (normalized.endsWith('_yearly')) return 365;
  return null;
}

export function formatUsd(value) {
  const amount = Number(value || 0);
  if (amount === 0) return '$0';

  const isInt = Number.isInteger(amount);
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: isInt ? 0 : 2,
    maximumFractionDigits: isInt ? 0 : 2,
  }).format(amount);
}

export function buildPlanHighlights(plan) {
  const highlights = [];

  if (Number.isFinite(plan?.interviews_per_month)) {
    highlights.push(`Interviews per month: ${plan.interviews_per_month}`);
  }

  const interviewTypes = [];
  if (plan?.allow_interview_quick) interviewTypes.push('Quick');
  if (plan?.allow_interview_standard) interviewTypes.push('Standard');
  if (plan?.allow_interview_full) interviewTypes.push('Full');
  if (interviewTypes.length) {
    highlights.push(`Interview types: ${interviewTypes.join(', ')}`);
  }

  const coachModes = [];
  if (plan?.allow_coach_friend) coachModes.push('Friend');
  if (plan?.allow_coach_guide) coachModes.push('Guide');
  if (plan?.allow_coach_challenger) coachModes.push('Challenger');
  if (plan?.allow_coach_sergeant) coachModes.push('Sergeant');
  if (coachModes.length) {
    highlights.push(`Coach modes: ${coachModes.join(', ')}`);
  }

  const topics = [];
  if (plan?.allow_topic_soft_skills) topics.push('Soft');
  if (plan?.allow_topic_hard_skills) topics.push('Hard');
  if (plan?.allow_topic_motivation) topics.push('Motivational');
  if (plan?.allow_topic_behavioral) topics.push('Behavioral');
  if (plan?.allow_topic_ai) topics.push('AI');
  if (topics.length) {
    highlights.push(`Topics: ${topics.join(', ')}`);
  }

  if (Number.isFinite(plan?.vacancies_upload_limit)) {
    highlights.push(`CV uploads: ${plan.vacancies_upload_limit}`);
  }
  if (Number.isFinite(plan?.internal_search_limit)) {
    highlights.push(`Internal search: ${plan.internal_search_limit}`);
  }

  if (plan?.allow_top_skills) {
    highlights.push('Top skills input');
  }
  if (plan?.allow_skills_in_progress) {
    highlights.push('Skills in progress tracking');
  }
  if (plan?.summary_level) {
    highlights.push(`Summary depth: ${capitalize(plan.summary_level)}`);
  }
  if (plan?.allow_bars_scoring) {
    highlights.push('BARS scoring');
  }

  return highlights;
}

export function mapCatalogPlanToPricing(plan) {
  const priceUsd = Number(plan?.price_usd || 0);
  const planCode = String(plan?.plan_code || '');

  return {
    id: String(plan?.id || planCode),
    name: plan?.plan_name || planCode,
    positioning: POSITIONING_BY_CODE[planCode] || 'Interview preparation',
    priceUsd,
    price: formatUsd(priceUsd),
    cadence: getCadenceFromPlanCode(planCode, priceUsd),
    highlights: buildPlanHighlights(plan),
    cta: priceUsd > 0 ? 'Subscribe now' : 'Included by default',
    planCode,
    isSubscription: priceUsd > 0,
    isPaid: priceUsd > 0,
    popular: planCode === 'pro_monthly',
    raw: plan,
  };
}
